// 产品卡片组件 - 列表视图
// v4.3.0 - 性能优化：使用 React.memo 防止不必要的重渲染
import React, { memo, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Star,
  Image as ImageIcon,
  Edit2,
  Trash2,
  Copy,
  Plus,
} from 'lucide-react';
import type { Product, ProductModel } from '@/poster/types';
import { ProductCategoryLabels } from '@/poster/types';

interface ProductCardProps {
  product: Product;
  isExpanded: boolean;
  onToggleExpand: (productId: string) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onCopy: (product: Product) => void;
  onAddModel: (productId: string) => void;
  onEditModel: (productId: string, model: ProductModel) => void;
  onDeleteModel: (productId: string, modelId: string) => void;
}

// 使用 React.memo 包装，只在 props 真正变化时才重渲染
export const ProductCard = memo(function ProductCard({
  product,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onCopy,
  onAddModel,
  onEditModel,
  onDeleteModel,
}: ProductCardProps) {
  // 内部回调 - 避免在 JSX 中创建新函数
  const handleToggleExpand = useCallback(() => {
    onToggleExpand(product.id);
  }, [onToggleExpand, product.id]);
  
  const handleEdit = useCallback(() => {
    onEdit(product);
  }, [onEdit, product]);
  
  const handleDelete = useCallback(() => {
    onDelete(product);
  }, [onDelete, product]);
  
  const handleCopy = useCallback(() => {
    onCopy(product);
  }, [onCopy, product]);
  
  const handleAddModel = useCallback(() => {
    onAddModel(product.id);
  }, [onAddModel, product.id]);
  
  const handleEditModel = useCallback((model: ProductModel) => {
    onEditModel(product.id, model);
  }, [onEditModel, product.id]);
  
  const handleDeleteModel = useCallback((modelId: string) => {
    onDeleteModel(product.id, modelId);
  }, [onDeleteModel, product.id]);
  const modelsCount = product.models?.length || 0;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Main Info */}
      <div className="flex items-center gap-4 p-4">
        {/* Expand Button */}
        <button
          onClick={handleToggleExpand}
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          title={isExpanded ? '收起' : '展开型号'}
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Image - 使用懒加载优化性能 */}
        <div className="w-28 h-20 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-gray-600" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 truncate text-base">{product.name}</h3>
            {product.is_featured && (
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-green-600 truncate">{product.series_name || '—'}</p>
          {product.slogan && (
            <p className="text-xs text-gray-400 truncate mt-0.5 italic">"{product.slogan}"</p>
          )}
        </div>

        {/* Category Badge */}
        <div className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
          product.category === 'all_in_one' 
            ? 'bg-purple-100 text-purple-700' 
            : product.category === 'module'
            ? 'bg-orange-100 text-orange-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {ProductCategoryLabels[product.category]}
        </div>

        {/* Specs */}
        <div className="text-sm text-gray-600 w-28 flex-shrink-0">
          <span className="font-medium">{product.pitch_range || '—'}</span>
        </div>

        <div className="text-sm text-gray-500 w-28 flex-shrink-0">
          {product.process_type || '—'}
        </div>

        {/* Models Count */}
        <div className={`text-sm w-20 flex-shrink-0 ${
          modelsCount > 0 ? 'text-blue-600 font-medium' : 'text-gray-400'
        }`}>
          {modelsCount > 0 ? `${modelsCount} 型号` : '暂无型号'}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleCopy}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="复制产品"
          >
            <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
          <button
            onClick={handleEdit}
            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="编辑产品"
          >
            <Edit2 className="w-4 h-4 text-blue-500" />
          </button>
          <button
            onClick={handleDelete}
            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="删除产品"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      {/* Expanded Content - Models */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gradient-to-b from-gray-50 to-white p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              产品型号
              {modelsCount > 0 && (
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                  {modelsCount}
                </span>
              )}
            </h4>
            <button 
              onClick={handleAddModel}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              添加型号
            </button>
          </div>

          {product.models && product.models.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {product.models.map((model) => (
                <div
                  key={model.id}
                  className="group bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-200 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      {model.model_name}
                    </span>
                    <div className="flex items-center gap-1">
                      {model.is_default && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                          默认
                        </span>
                      )}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditModel(model)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="编辑型号"
                        >
                          <Edit2 className="w-3 h-3 text-gray-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model.id)}
                          className="p-1 hover:bg-red-50 rounded"
                          title="删除型号"
                        >
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-gray-500">
                    {model.pitch && (
                      <p>间距: <span className="text-gray-700">P{model.pitch}</span></p>
                    )}
                    {model.cabinet_size && (
                      <p>箱体: <span className="text-gray-700">{model.cabinet_size}</span></p>
                    )}
                    {model.weight && (
                      <p>重量: <span className="text-gray-700">{model.weight}</span></p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <p className="text-sm text-gray-400 mb-2">暂无型号数据</p>
              <button 
                onClick={handleAddModel}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                点击添加第一个型号
              </button>
            </div>
          )}

          {/* Features & Slogan */}
          {(product.features?.length || 0) > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">产品特点</p>
              <div className="flex flex-wrap gap-2">
                {product.features?.map((feature, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
